import React from "react";
import { useObserver, Observer, useLocalStore } from "mobx-react";
import { AutoSizer } from "react-virtualized";
import { Helmet } from "react-helmet";
import { Fab, makeStyles, Paper } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import locale from "@bonliva-crm/locale";
import { ThemeType } from "@theme";
import SearchPageLayout from "@search/components/SearchPageLayout";
import ActionBar from "@search/ActionBar";
import ColumnSizer from "@search/TableSearch/ColumnSizer";
import TableSearchHeader from "@search/TableSearch/TableSearchHeader";
import DnDContainer from "@containers/DnDContainer/DnDContainer";
import { CustomerContractsSearchForm } from "./CustomerContractsSearchForm";
import { CustomerContractsSearchStore } from "./CustomerContractsSearchStore";
import { columns } from "./CustomerContractsSearchColumns";
import {
  CustomerContractOption,
  getSortOptions
} from "@contracts/CustomerContractsSearch/utils";
import { useUserProfile } from "@hooks";
import { DefaultInfinityTableRow, InfiniteScroll } from "@search/InfiniteScroll";

type Props = {
  onOpen: (id: string) => any;
  onNew: () => any;
};

export function CustomerContractsSearch(props: Props) {
  const { onOpen, onNew } = props;
  const localStore = useLocalStore(() => new CustomerContractsSearchStore());
  const { can } = useUserProfile();
  const classes = useStyles({});

  const tableHead = (
    <AutoSizer disableHeight className={classes.stickyHeader}>
      {({ width }) => (
        <div style={{ background: "white", position: "relative", width }}>
          <ColumnSizer columns={columns} width={width}>
            {({ ref }) => (
              <TableSearchHeader
                columns={columns}
                columnSizer={ref}
                style={{ backgroundColor: "#fff" }}
              />
            )}
          </ColumnSizer>
        </div>
      )}
    </AutoSizer>
  );

  const rowRenderer = (item, index) => {
    return (
      <DefaultInfinityTableRow
        item={item}
        onItemOpen={(id) => onOpen(id)}
        key={item._id}
        rowIndex={index}
        columns={columns}
      />
    );
  };

  return useObserver(() => {
    return (
      <>
        <Helmet>
          <title>CRM | Kundkontrakter</title>
        </Helmet>

        <SearchPageLayout
          delayed
          filter={<CustomerContractsSearchForm manager={localStore.filterManager} />}
          actionBar={
            <ActionBar
              style={{ padding: "8px 16px" }}
              onSortOption={(opt) =>
                localStore.updateSelectedSortOption(opt.value as CustomerContractOption)
              }
              sortOptions={getSortOptions(localStore.selectedSortOption)}
              total={localStore.totalCount}
              totalLoading={localStore.isTotalLoading}
              actions={[
                <Fab
                  key="new"
                  color="primary"
                  title={locale.New}
                  size="small"
                  onClick={onNew}
                  disabled={!can("customer-contract:edit:all")}
                >
                  <AddIcon />
                </Fab>
              ]}
            />
          }
          content={(scrollContainer) => (
            <Observer>
              {() => (
                <InfiniteScroll
                  scrollContainer={scrollContainer}
                  search={localStore.filterManager?.filter.text}
                  items={localStore.searchManager.items}
                  loadMore={localStore.searchManager.loadMoreRows}
                  hasMore={localStore.hasMore}
                  dataLength={localStore.searchManager.rowCount}
                  renderHead={tableHead}
                  loading={localStore.searchManager.isLoading}
                  renderRow={rowRenderer}
                />
              )}
            </Observer>
          )}
          newButton={null}
          showContentOverlay={false}
          wrapContent={(content) => (
            <DnDContainer
              onDrop={localStore.addUploadFiles}
              style={{ minHeight: "100%" }}
            >
              <Paper>{content}</Paper>
            </DnDContainer>
          )}
        />
      </>
    );
  });
}

const useStyles = makeStyles((theme: ThemeType) => ({
  stickyHeader: { position: "sticky", top: 0, zIndex: 1 },

  row: {
    "& *": {
      fontSize: 14
    }
  }
}));
